import React, { useState, useEffect } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";

const AddAnalyzedOrganicRCN = () => {
  const initialFormState = {
    date: "",
    month: "",
    year: "",
    numberOfTrucks: "",
    rcn_type: "Organic",
    statusApproval: "",
    agent: "",
    conventionalCode: "",
    truckNumber: "",
    reanalysis: "",
    region: "",
    provinceOrCity: "",
    country: "",
    countPerKg: "",
    moisture: "",
    outturn: "",
    size15: "",
    size17: "",
    size19: "",
    size21: "",
    size23: "",
    size25: "",
    size28: "",
    totalPercentage: "",
    impuritiesPercentage: "",
    boredPercentage: "",
    voidPercentage: "",
    oilPercentage: "",
    rottenPercentage: "",
    totalImpuritiesPercentage: "",
    immaturePercentage: "",
    spottedPercentage: "",
    totalSpPlusImPercentage: "",
    totalDefectiveRatePercentage: "",
    veryBad: "",
    bad: "",
    regular: "",
    good: "",
    veryGood: "",
    expectedKernelOutturn: "",
    origin: "",
    qualityRating: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  const handleChange = (e) => {
    const { id, name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [type === 'radio' ? name : id]: value
    }));
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const transformedData = {
        date: formData.date,
        month: formData.month,
        year: parseInt(formData.year),
        number_of_trucks: parseInt(formData.numberOfTrucks),
        rcn_type: "Organic",
        status_approval: formData.statusApproval,
        agent: formData.agent,
        organic_code: formData.conventionalCode,
        truck_number: formData.truckNumber,
        reanalysis: formData.reanalysis,
        region: formData.region,
        province_or_city: formData.provinceOrCity,
        country: formData.country,
        count_per_kg: parseFloat(formData.countPerKg),
        moisture: parseFloat(formData.moisture),
        outturn: parseFloat(formData.outturn),
        size_15: parseFloat(formData.size15),
        size_17: parseFloat(formData.size17),
        size_19: parseFloat(formData.size19),
        size_21: parseFloat(formData.size21),
        size_23: parseFloat(formData.size23),
        size_25: parseFloat(formData.size25),
        size_28: parseFloat(formData.size28),
        total_size: parseFloat(formData.totalPercentage),
        impurities: parseFloat(formData.impuritiesPercentage),
        bored: parseFloat(formData.boredPercentage),
        void: parseFloat(formData.voidPercentage),
        oil: parseFloat(formData.oilPercentage),
        rotten: parseFloat(formData.rottenPercentage),
        total_impurities: parseFloat(formData.totalImpuritiesPercentage),
        immature_percentage: parseFloat(formData.immaturePercentage),
        spotted_percentage: parseFloat(formData.spottedPercentage),
        total_sp_plus_im_percentage: parseFloat(formData.totalSpPlusImPercentage),
        total_defective_rate_percentage: parseFloat(formData.totalDefectiveRatePercentage),
        very_bad: parseFloat(formData.veryBad),
        bad: parseFloat(formData.bad),
        regular: parseFloat(formData.regular),
        good: parseFloat(formData.good),
        very_good: parseFloat(formData.veryGood),
        expected_kernel_outturn: parseFloat(formData.expectedKernelOutturn),
        origin: formData.origin,
        quality_rcn: formData.qualityRating,
      };

      console.log('Submitting transformed data:', transformedData);

      const response = await fetch("https://usibras-web-backend.onrender.com/api/addAnalyseRCN", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transformedData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Server error response:', data);
        throw new Error(data.message || data.error || 'Something went wrong');
      }
      
      showAlert(`Successfully analyzed! ${data.message}`, 'success');
      setFormData(initialFormState);
    } catch (error) {
      console.error('Error details:', error);
      showAlert(error.message || "An error occurred", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Add CSS styles for hiding number input spinners
  const styles = `
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type="number"] {
      -moz-appearance: textfield;
    }
  `;

  // Prevent scroll from changing number input values
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, []);

  return (
    <div className="mt-5 h-full">
      <style>{styles}</style>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      {alert.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
          alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50`}>
          {alert.message}
        </div>
      )}

      <Card extra={"w-full p-4"}>
        <div className="mb-8 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Analyze Organic RCN
          </h4>
          <p className="mt-2 text-base text-gray-600">
            Please fill in all the required information
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Basic Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Date*"
                id="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Month*
                </label>
                <select
                  id="month"
                  value={formData.month}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  required
                >
                  <option value="">Select month</option>
                  <option value="January">January</option>
                  <option value="February">February</option>
                  <option value="March">March</option>
                  <option value="April">April</option>
                  <option value="May">May</option>
                  <option value="June">June</option>
                  <option value="July">July</option>
                  <option value="August">August</option>
                  <option value="September">September</option>
                  <option value="October">October</option>
                  <option value="November">November</option>
                  <option value="December">December</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Year*
                </label>
                <select
                  id="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  required
                >
                  <option value="">Select year</option>
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <InputField
                label="Number of Trucks*"
                id="numberOfTrucks"
                type="number"
                placeholder="Enter number of trucks"
                value={formData.numberOfTrucks}
                onChange={handleChange}
                required
              />
              <InputField
                label="RCN Type*"
                id="rcn_type"
                type="text"
                value={formData.rcn_type}
                disabled
                required
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Status Approval*
                </label>
                <select
                  id="statusApproval"
                  value={formData.statusApproval}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  required
                >
                  <option value="">Select status</option>
                  <option value="Approve">Approve</option>
                  <option value="Reject">Reject</option>
                </select>
              </div>
            </div>
          </div>

          {/* Status and Agent Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Status and Agent Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Agent/Association*"
                id="agent"
                type="text"
                placeholder="Enter agent"
                value={formData.agent}
                onChange={handleChange}
                required
              />
              <InputField
                label="Organic Code"
                id="conventionalCode"
                type="text"
                placeholder="Enter Organic code"
                value={formData.conventionalCode}
                onChange={handleChange}
              />
              <InputField
                label="Truck Number*"
                id="truckNumber"
                type="text"
                placeholder="Enter truck number"
                value={formData.truckNumber}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Location Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Location Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Reanalysis*
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="reanalysis"
                      value="Yes"
                      checked={formData.reanalysis === "Yes"}
                      onChange={handleChange}
                      className="mr-2"
                      required
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="reanalysis"
                      value="No"
                      checked={formData.reanalysis === "No"}
                      onChange={handleChange}
                      className="mr-2"
                      required
                    />
                    No
                  </label>
                </div>
              </div>
              <InputField
                label="Origin*"
                id="origin"
                type="text"
                placeholder="Enter origin"
                value={formData.origin}
                onChange={handleChange}
                required
              />
              <InputField
                label="Region*"
                id="region"
                type="text"
                placeholder="Enter region"
                value={formData.region}
                onChange={handleChange}
                required
              />
              <InputField
                label="Province or City*"
                id="provinceOrCity"
                type="text"
                placeholder="Enter province or city"
                value={formData.provinceOrCity}
                onChange={handleChange}
                required
              />
              <InputField
                label="Country*"
                id="country"
                type="text"
                placeholder="Enter country"
                value={formData.country}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Count/KG*"
                id="countPerKg"
                type="number"
                step="0.01"
                placeholder="Enter count per kg"
                value={formData.countPerKg}
                onChange={handleChange}
                required
              />
              <InputField
                label="Moisture %*"
                id="moisture"
                type="number"
                step="0.01"
                placeholder="Enter moisture percentage"
                value={formData.moisture}
                onChange={handleChange}
                required
              />
              <InputField
                label="Outturn %*"
                id="outturn"
                type="number"
                step="0.01"
                placeholder="Enter outturn percentage"
                value={formData.outturn}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      outturn: value
                    };
                    // Calculate expected kernel outturn using the formula: (outturn/2.2046 * 1.25) / 100 * 100
                    const step1 = parseFloat(value || 0) / 2.2046;  // First division
                    const step2 = step1 * 1.25;  // Multiplication
                    const expectedKernelOutturn = ((step2 / 100) * 100).toFixed(2);  // Final division and multiplication by 100
                    
                    return {
                      ...newFormData,
                      expectedKernelOutturn: expectedKernelOutturn
                    };
                  });
                }}
                required
              />
            </div>
          </div>

          {/* Size Distribution */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Size Distribution in Percentage(%)
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Size 15*"
                id="size15"
                type="number"
                step="0.01"
                placeholder="Enter size 15"
                value={formData.size15}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size15: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <InputField
                label="Size 17*"
                id="size17"
                type="number"
                step="0.01"
                placeholder="Enter size 17"
                value={formData.size17}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size17: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <InputField
                label="Size 19*"
                id="size19"
                type="number"
                step="0.01"
                placeholder="Enter size 19"
                value={formData.size19}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size19: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <InputField
                label="Size 21*"
                id="size21"
                type="number"
                step="0.01"
                placeholder="Enter size 21"
                value={formData.size21}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size21: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <InputField
                label="Size 23*"
                id="size23"
                type="number"
                step="0.01"
                placeholder="Enter size 23"
                value={formData.size23}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size23: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <InputField
                label="Size 25*"
                id="size25"
                type="number"
                step="0.01"
                placeholder="Enter size 25"
                value={formData.size25}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size25: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <InputField
                label="Size 28*"
                id="size28"
                type="number"
                step="0.01"
                placeholder="Enter size 28"
                value={formData.size28}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      size28: value
                    };
                    // Calculate total percentage
                    const total = (
                      parseFloat(newFormData.size15 || 0) +
                      parseFloat(newFormData.size17 || 0) +
                      parseFloat(newFormData.size19 || 0) +
                      parseFloat(newFormData.size21 || 0) +
                      parseFloat(newFormData.size23 || 0) +
                      parseFloat(newFormData.size25 || 0) +
                      parseFloat(newFormData.size28 || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalPercentage: total
                    };
                  });
                }}
                required
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total %*
                </label>
                <input
                  id="totalPercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalPercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Quality Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Quality Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Impurities %*"
                id="impuritiesPercentage"
                type="number"
                step="0.01"
                placeholder="Enter impurities percentage"
                value={formData.impuritiesPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      impuritiesPercentage: value
                    };
                    // Calculate total impurities percentage
                    const totalImpurities = (
                      parseFloat(newFormData.impuritiesPercentage || 0) +
                      parseFloat(newFormData.boredPercentage || 0) +
                      parseFloat(newFormData.voidPercentage || 0) +
                      parseFloat(newFormData.oilPercentage || 0) +
                      parseFloat(newFormData.rottenPercentage || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities
                    };
                  });
                }}
                required
              />
              <InputField
                label="Bored %*"
                id="boredPercentage"
                type="number"
                step="0.01"
                placeholder="Enter bored percentage"
                value={formData.boredPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      boredPercentage: value
                    };
                    // Calculate total impurities percentage
                    const totalImpurities = (
                      parseFloat(newFormData.impuritiesPercentage || 0) +
                      parseFloat(newFormData.boredPercentage || 0) +
                      parseFloat(newFormData.voidPercentage || 0) +
                      parseFloat(newFormData.oilPercentage || 0) +
                      parseFloat(newFormData.rottenPercentage || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities
                    };
                  });
                }}
                required
              />
              <InputField
                label="Void %*"
                id="voidPercentage"
                type="number"
                step="0.01"
                placeholder="Enter void percentage"
                value={formData.voidPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      voidPercentage: value
                    };
                    // Calculate total impurities percentage
                    const totalImpurities = (
                      parseFloat(newFormData.impuritiesPercentage || 0) +
                      parseFloat(newFormData.boredPercentage || 0) +
                      parseFloat(newFormData.voidPercentage || 0) +
                      parseFloat(newFormData.oilPercentage || 0) +
                      parseFloat(newFormData.rottenPercentage || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities
                    };
                  });
                }}
                required
              />
              <InputField
                label="Oil %*"
                id="oilPercentage"
                type="number"
                step="0.01"
                placeholder="Enter oil percentage"
                value={formData.oilPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      oilPercentage: value
                    };
                    // Calculate total impurities percentage
                    const totalImpurities = (
                      parseFloat(newFormData.impuritiesPercentage || 0) +
                      parseFloat(newFormData.boredPercentage || 0) +
                      parseFloat(newFormData.voidPercentage || 0) +
                      parseFloat(newFormData.oilPercentage || 0) +
                      parseFloat(newFormData.rottenPercentage || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities
                    };
                  });
                }}
                required
              />
              <InputField
                label="Rotten %*"
                id="rottenPercentage"
                type="number"
                step="0.01"
                placeholder="Enter rotten percentage"
                value={formData.rottenPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      rottenPercentage: value
                    };
                    // Calculate total impurities percentage
                    const totalImpurities = (
                      parseFloat(newFormData.impuritiesPercentage || 0) +
                      parseFloat(newFormData.boredPercentage || 0) +
                      parseFloat(newFormData.voidPercentage || 0) +
                      parseFloat(newFormData.oilPercentage || 0) +
                      parseFloat(newFormData.rottenPercentage || 0)
                    ).toFixed(2);
                    return {
                      ...newFormData,
                      totalImpuritiesPercentage: totalImpurities
                    };
                  });
                }}
                required
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total Impurities %*
                </label>
                <input
                  id="totalImpuritiesPercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalImpuritiesPercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
              <InputField
                label="Immature %*"
                id="immaturePercentage"
                type="number"
                step="0.01"
                placeholder="Enter immature percentage"
                value={formData.immaturePercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      immaturePercentage: value
                    };
                    // Calculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Calculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(newFormData.totalImpuritiesPercentage || 0) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate expected kernel outturn - Commented out
                    // const expectedKernelOutturn = (parseFloat(totalDefectiveRate) / 2).toFixed(2);
                    
                    return {
                      ...newFormData,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      // expectedKernelOutturn: expectedKernelOutturn
                    };
                  });
                }}
                required
              />
              <InputField
                label="Spotted %*"
                id="spottedPercentage"
                type="number"
                step="0.01"
                placeholder="Enter spotted percentage"
                value={formData.spottedPercentage}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => {
                    const newFormData = {
                      ...prev,
                      spottedPercentage: value
                    };
                    // Calculate total SP+IM percentage
                    const totalSpPlusIm = (
                      parseFloat(newFormData.immaturePercentage || 0) +
                      parseFloat(newFormData.spottedPercentage || 0)
                    ).toFixed(2);
                    
                    // Calculate total defective rate percentage
                    const totalDefectiveRate = (
                      parseFloat(newFormData.totalImpuritiesPercentage || 0) +
                      (parseFloat(totalSpPlusIm) / 2)
                    ).toFixed(2);
                    
                    // Calculate expected kernel outturn - Commented out
                    // const expectedKernelOutturn = (parseFloat(totalDefectiveRate) / 2).toFixed(2);
                    
                    return {
                      ...newFormData,
                      totalSpPlusImPercentage: totalSpPlusIm,
                      totalDefectiveRatePercentage: totalDefectiveRate,
                      // expectedKernelOutturn: expectedKernelOutturn
                    };
                  });
                }}
                required
              />
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total SP+IM %*
                </label>
                <input
                  id="totalSpPlusImPercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalSpPlusImPercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Total Defective Rate %*
                </label>
                <input
                  id="totalDefectiveRatePercentage"
                  type="number"
                  step="0.01"
                  value={formData.totalDefectiveRatePercentage}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Quality Ratings */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Quality Ratings
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Quality Rating*
                </label>
                <div className="flex flex-wrap gap-6 mt-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="qualityRating"
                      name="qualityRating"
                      value="Very Bad"
                      checked={formData.qualityRating === "Very Bad"}
                      onChange={handleChange}
                      className="h-4 w-4 text-brand-500 focus:ring-brand-400"
                      required
                    />
                    <label htmlFor="veryBad" className="ml-2 text-gray-700 dark:text-white">
                      Very Bad
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="qualityRating"
                      name="qualityRating"
                      value="Bad"
                      checked={formData.qualityRating === "Bad"}
                      onChange={handleChange}
                      className="h-4 w-4 text-brand-500 focus:ring-brand-400"
                    />
                    <label htmlFor="bad" className="ml-2 text-gray-700 dark:text-white">
                      Bad
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="qualityRating"
                      name="qualityRating"
                      value="Regular"
                      checked={formData.qualityRating === "Regular"}
                      onChange={handleChange}
                      className="h-4 w-4 text-brand-500 focus:ring-brand-400"
                    />
                    <label htmlFor="regular" className="ml-2 text-gray-700 dark:text-white">
                      Regular
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="qualityRating"
                      name="qualityRating"
                      value="Good"
                      checked={formData.qualityRating === "Good"}
                      onChange={handleChange}
                      className="h-4 w-4 text-brand-500 focus:ring-brand-400"
                    />
                    <label htmlFor="good" className="ml-2 text-gray-700 dark:text-white">
                      Good
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="qualityRating"
                      name="qualityRating"
                      value="Very Good"
                      checked={formData.qualityRating === "Very Good"}
                      onChange={handleChange}
                      className="h-4 w-4 text-brand-500 focus:ring-brand-400"
                    />
                    <label htmlFor="veryGood" className="ml-2 text-gray-700 dark:text-white">
                      Very Good
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-navy-700 dark:text-white">
                  Expected Kernel Outturn %*
                </label>
                <input
                  id="expectedKernelOutturn"
                  type="number"
                  step="0.01"
                  value={formData.expectedKernelOutturn}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-brand-500 hover:bg-brand-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Analysis"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddAnalyzedOrganicRCN; 